
import {
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { dataSource } from "@/lib/dataSource";
import { Type, InsertRuleFunction } from "../shared/schema";
import { httpOpenAiProvider } from "@/lib/nlpProvider";

export interface Comment {
  id: string;
  ruleId: string;
  userId: string;
  userName: string;
  text: string;
  createdAt: string;
  updatedAt: string;
}

export interface InsertComment {
  ruleId: number | undefined;
  text: string;
}

//Get Filter APIs
export function useRulesData(
  pageIndex: number,
  pageSize?: number,
  typeKey?: string
) {
  return useQuery({
    queryKey: ["ruletypes", pageIndex, pageSize, typeKey],
    queryFn: () => dataSource.listRules(pageIndex, pageSize, typeKey),
  });
}

export function useRulesFunctionData(
  pageIndex: number,
  pageSize?: number,
  typeKey?: string
) {
  return useQuery({
    queryKey: ["rulefunctions", pageIndex, pageSize, typeKey],
    queryFn: () => dataSource.listRuleFunction(pageIndex, pageSize, typeKey),
  });
}

export function useRulesVersion(
  pageIndex: number,
  pageSize?: number,
  typeKey?: string,
  typeRule?: number
) {
  return useQuery({
    queryKey: ["ruleVersion", pageIndex, pageSize, typeKey, typeRule],
    queryFn: () => dataSource.listRulesVersion(pageIndex, pageSize, typeKey, typeRule),
  });
}

export function useRulesPublish(
  pageIndex: number,
  pageSize?: number,
  typeKey?: string,
  typeRule?: number
) {
  return useQuery({
    queryKey: ["rulePublish", pageIndex, pageSize, typeKey, typeRule],
    queryFn: () => dataSource.listRulesPublish(pageIndex, pageSize, typeKey, typeRule),
  });
}

export function useVersionHistory(
  id: string,
  pageIndex: number,
  pageSize?: number,
) {
  return useQuery({
    queryKey: ["ruleVersionHistory", id, pageIndex, pageSize],
    queryFn: () => dataSource.listRulesVersionHistory(id, pageIndex, pageSize),
  });
}

export function useDataTypes(
  pageIndex: number,
  pageSize?: number,
  typeKey?: string
) {
  return useQuery({
    queryKey: ["datatypes", pageIndex, pageSize, typeKey],
    queryFn: () => dataSource.listDataTypes(pageIndex, pageSize, typeKey),
  });
}

export function useAttributes(parentCode: string) {
  return useQuery({
    queryKey: ["getattribute", parentCode],
    queryFn: () => dataSource.listAttributes(parentCode),
  });
}



// Get Filter APIs
export function useGetFilterRule(
  typeKey?: string
) {
  return useQuery({
    queryKey: ["ruleFilter", typeKey],
    queryFn: () => dataSource.listRuleFilter(typeKey),
  });
}

export function useGetFilterRuleFuction(
  typeKey?: string
) {
  return useQuery({
    queryKey: ["rulefunctionFilter", typeKey],
    queryFn: () => dataSource.listRuleFunctionFilter(typeKey),
  });
}

export function useGetFilterRuleVersion(
  typeKey?: string
) {
  return useQuery({
    queryKey: ["ruleVersionFilter", typeKey],
    queryFn: () => dataSource.listRuleVersionFilter(typeKey),
  });
}

export function useGetFilterRulePublish(
  typeKey?: string
) {
  return useQuery({
    queryKey: ["rulePublishFilter", typeKey],
    queryFn: () => dataSource.listRulePublishFilter(typeKey),
  });
}

//Add Mutation APIS
export function useCreateRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: dataSource.createRule.bind(dataSource),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ruletypes"], exact: false });
    },
  });
}

export function useCreateAIRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: dataSource.createAIRule.bind(dataSource),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ruletypes"], exact: false });
    },
  });
}

export function useCreateRuleFunction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: any) => dataSource.createRuleFunction(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rulefunctions"], exact: false });
      queryClient.invalidateQueries({ queryKey: ["rulefunctionFilter"], exact: false });
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === "rulefunctions",
      });
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === "rulefunctionFilter",
      });
    },
  });
}

export function useRuleBind(id: string) {
  return useQuery({
    queryKey: ["getrulebind", id],
    queryFn: () => dataSource.listRuleBind(id),
  });
}

//Update Mutation APIS
export function useUpdateRuleFunction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: number; updates: Partial<InsertRuleFunction> }) =>
      dataSource.updateRuleFunction(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rulefunctions"], exact: false });
      queryClient.invalidateQueries({ queryKey: ["rulefunctionFilter"], exact: false });
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === "rulefunctions",
      });
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === "rulefunctionFilter",
      });
    },
  });
}

export function useCreateBind() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { id: string; body: object }) =>
      dataSource.createRuleBind(params.id, params.body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ruletypes"], exact: false });
      queryClient.invalidateQueries({ queryKey: ["getrulebind"], exact: false });
    },
  });
}

export function useCreateVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { Rule_Id: number; Data: string; ObjectId: number; Remarks: string; }) =>
      dataSource.createRuleVersion(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ruleVersion"], exact: false });
    },
  });
}

export function useUpdateVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ version, payload }: {
      version: string;
      payload: { Rule_Id: number; Data: string; ObjectId: number; Remarks: string; }
    }) => dataSource.updateRuleVersion(version, payload),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ruleVersion"], exact: false });
    },
  });
}

export function useUpdateRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: number | undefined; updates: Partial<Type> }) =>
      dataSource.updateRule(id, updates),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["ruletypes"] });
      queryClient.invalidateQueries({ queryKey: ["rule", id] });
    },
  });
}

// Status change APis
export function useApproveRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, status }: { key: number | undefined; status: number }) =>
      dataSource.updateRuleStatus(key, status),
    onSuccess: (_, { key }) => {
      queryClient.invalidateQueries({ queryKey: ["ruletypes"] });
      queryClient.invalidateQueries({ queryKey: ["ruleVersion"] });
    },
  });
}

export function useApproveRulePublish() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, status }: { key: number; status: number }) =>
      dataSource.updateRulePublishStatus(key, status),
    onSuccess: (_, { key }) => {
      queryClient.invalidateQueries({ queryKey: ["rulePublish"] });
    },
  });
}

export function useApproveRuleFunction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, status }: { key: number; status: number }) =>
      dataSource.updateRuleFunctionStatus(key, status),
    onSuccess: (_, { key }) => {
      queryClient.invalidateQueries({ queryKey: ["rulefunctions"] });
      queryClient.invalidateQueries({ queryKey: ["rulefunctionFilter"] });
    },
  });
}


//Delete APIS
export function useDeleteRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, authCode }: { key: number | undefined; authCode?: string }) =>
      dataSource.deleteRules(key, authCode),
    onMutate: async ({ key }) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: ["ruletypes"] }),
        queryClient.cancelQueries({ queryKey: ["ruleversion"] }),
      ]);
      const previousTypes = queryClient.getQueryData<any>(["ruletypes"]);
      if (previousTypes) {
        queryClient.setQueryData(["datatypes"], (old: any) =>
          (old || []).filter((t: any) => t.key !== key)
        );
      }
      return { previousTypes } as const;
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx) return;
      if (ctx.previousTypes)
        queryClient.setQueryData(["ruletypes"], ctx.previousTypes);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["ruletypes"] });
      queryClient.invalidateQueries({ queryKey: ["ruleversion"] });
    },
  });
}

export function useDeleteRuleFunction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key }: { key: number; }) =>
      dataSource.deleteRuleFunction(key),
    onMutate: async ({ key }) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: ["rulefunctions"] }),
        queryClient.cancelQueries({ queryKey: ["rulefunctionFilter"] }),
      ]);
      const previousTypes = queryClient.getQueryData<any>(["rulefunctions"]);
      if (previousTypes) {
        queryClient.setQueryData(["datatypes"], (old: any) =>
          (old || []).filter((t: any) => t.key !== key)
        );
      }
      return { previousTypes } as const;
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx) return;
      if (ctx.previousTypes)
        queryClient.setQueryData(["rulefunctions"], ctx.previousTypes);
      queryClient.setQueryData(["rulefunctionFilter"], ctx.previousTypes);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["rulefunctions"] });
      queryClient.invalidateQueries({ queryKey: ["rulefunctionFilter"] });
    },
  });
}

//Comment APIs
export function useRuleComments(ruleId: string) {
  return useQuery({
    queryKey: ["rules", ruleId, "comments"],
    queryFn: () => dataSource.getRuleComments(ruleId),
    enabled: !!ruleId,
  });
}

export function useCreateComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (commentData: InsertComment) =>
      dataSource.createRuleComment(commentData.ruleId, commentData.text),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["ruletypes"] });
      queryClient.invalidateQueries({
        queryKey: ["rules", variables.ruleId, "comments"],
      });
    },
  });
}




//Other Section APIs
export function useTypeEdges() {
  return useQuery({
    queryKey: ["typeEdges"],
    queryFn: () => dataSource.listTypeEdges(),
  });
}

export function useSearch(query: string) {
  return useQuery({
    queryKey: ["search", query],
    queryFn: () => dataSource.search(query),
    enabled: query.length >= 2,
  });
}

export function useDataQuality() {
  return useQuery({
    queryKey: ["dataQuality"],
    queryFn: () => dataSource.analyzeDataQuality(),
  });
}

export function useAuditLog(filters?: any) {
  return useQuery({
    queryKey: ["auditLog", filters],
    queryFn: () => dataSource.getAuditLog(filters),
  });
}

export function useStats() {
  return useQuery({
    queryKey: ["stats"],
    queryFn: () => dataSource.getStats(),
  });
}



//Rules APIs
export function useCreateRuleAI() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ prompt, session_id }: { prompt: string; session_id: string | null }) =>
      httpOpenAiProvider.createRuleAI(prompt, session_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ruleAI"] });
    },
  });
}

export function useTestRuleAI() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ( payload: { prompt: string; test_data: object }) =>
      httpOpenAiProvider.testRuleAI(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["testruleAI"] });
    },
  });
}